import triggerTracking from '@blueprint/tracking';
import { Text } from '@blueprint/ui';
import { SearchPageType, SearchSource } from '@zoopla/listing-search-context';
import {
    FiltersLabel,
    getBedsMax,
    getBedsMin,
    getDuration,
    getIsRetirementHome,
    getPriceFrequency,
    getPriceMax,
    getPriceMin,
    getPropertySubType,
    getPropertyType,
    getQ,
    getRadius,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import {
    BedsMax,
    BedsMin,
    PriceMax,
    PriceMin,
    SelectBeds,
    SelectPriceForSale,
    SelectPriceToRent
} from '@zoopla/listing-search/filters/options';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import {
    PropertySubType,
    PropertyType,
    PropertyTypeCheckboxes
} from '@zoopla/listing-search/property-type';
import { getUpdatedQuery } from '@zoopla/listing-search/search-initiation';
import { PriceFrequency } from '@zoopla/redux-reducer-filters';
import { useRouter } from 'next/router';
import { FC, MouseEvent, useState } from 'react';
import { CloseIconWithText } from '../CloseIconWithText';
import { formatLocation } from '../utils/formatLocation';
import { formatSearchPath } from '../utils/formatSearchPath';
import {
    FilterDivider,
    HeaderWrapper,
    LocationFilterWrapper,
    ModalCloseIconWrapper,
    ModalContentWrapper,
    StickyWrapperUpdateButton,
    UpdateButton
} from './SearchInitiationMobileModal.styles';
import { Duration, SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectPropertyType } from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';
import { SelectTransportMode, TransportType } from './SelectTransportMode';

type Tab = SearchPageType | 'house-prices';

const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

export type SearchInitiationMobileModalProps = {
    showRadius?: boolean;
    updateResultsText: string;
    headlineText?: string;
    headerTextOverride?: string;
    close: () => void;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
};

export const SearchInitiationMobileModal: FC<
    React.PropsWithChildren<SearchInitiationMobileModalProps>
> = ({
    showRadius = true,
    close,
    updateResultsText,
    headlineText,
    headerTextOverride,
    searchType,
    eventCategory,
    searchSource
}) => {
    const { query, push, pathname } = useRouter();

    const [formValue, updateFormValue] = useState({
        beds_max: getBedsMax(query),
        beds_min: getBedsMin(query),
        duration: getDuration(query),
        is_retirement_home: getIsRetirementHome(query),
        price_frequency: getPriceFrequency(query),
        price_max: getPriceMax(query),
        price_min: getPriceMin(query),
        property_sub_type: getPropertySubType(query),
        property_type: getPropertyType(query),
        q: getQ(query),
        radius: getRadius(query),
        transport_type: getTransportType(query)
    });

    const searchPath = getSearchPath(query);

    const isTravelTimeSearch = Boolean(
        getDuration(query) && getTransportType(query)
    );

    const formattedSearchPath = formatSearchPath(searchPath);

    const [locationText, setLocationText] = useState(
        formValue.q || formattedSearchPath
    );
    const [identifier, setIdentifier] = useState(formatLocation(searchPath));

    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';

    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const updatedQuery = getUpdatedQuery({
            query,
            formValue,
            isNewHomesDevelopersSearch,
            identifier,
            searchSource,
            viewType
        });

        close();

        await push({
            pathname: pathname,
            query: {
                ...updatedQuery
            }
        });
    };

    const headerText = isForSaleSearch
        ? 'Search homes for sale'
        : 'Search homes to rent';

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const handleClose = () => {
        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_close'
        });

        close();
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <ModalContentWrapper>
            <HeaderWrapper>
                {!headlineText ? (
                    <Text isBold color="grey-slate">
                        {headerTextOverride || headerText}
                    </Text>
                ) : null}
                <ModalCloseIconWrapper>
                    <CloseIconWithText
                        onClick={handleClose}
                        testId="quicksearch-modal_close-button_icon-only"
                        text="Close"
                    />
                </ModalCloseIconWrapper>
            </HeaderWrapper>

            <LocationFilterWrapper>
                <LocationSearchInput
                    id="filters-location-mobile"
                    onLocationInputKeyDown={handleSearchProperties}
                    onlyDisplayRemoveIconWhenActive={false}
                    value={locationText as string}
                    showError={showError}
                    onLocationSelect={({ identifier, q }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: q
                        }));
                        setIdentifier(identifier);
                    }}
                    handleInputTextChange={(value) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            q: ''
                        }));
                        setIdentifier(value);
                        setLocationText(value);
                    }}
                />
            </LocationFilterWrapper>

            <FilterDivider />

            {isTravelTimeSearch ? (
                <>
                    <SelectMaxTravelTime
                        value={formValue.duration as Duration}
                        onChange={(duration) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                duration: duration
                            }));
                        }}
                    />
                    <FilterDivider />
                    <SelectTransportMode
                        value={formValue.transport_type as TransportType}
                        onChange={(transportType) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                transport_type: transportType
                            }));
                        }}
                    />{' '}
                </>
            ) : showRadius ? (
                <SelectRadius
                    value={formValue.radius as Radius}
                    onChange={(radius) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            radius: radius
                        }));
                    }}
                />
            ) : null}

            <FilterDivider />

            {isForSaleSearch ? (
                <SelectPriceForSale
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax
                    }}
                    onChange={({ priceMin, priceMax }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax
                        }));
                    }}
                />
            ) : (
                <SelectPriceToRent
                    value={{
                        priceMin: formValue.price_min as PriceMin,
                        priceMax: formValue.price_max as PriceMax,
                        priceFrequency: (formValue.price_frequency ||
                            'per_month') as PriceFrequency
                    }}
                    onChange={({ priceMin, priceMax, priceFrequency }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            price_min: priceMin,
                            price_max: priceMax,
                            price_frequency: priceFrequency
                        }));
                    }}
                />
            )}

            <FilterDivider />

            <SelectBeds
                value={{
                    bedsMin: formValue.beds_min as BedsMin,
                    bedsMax: formValue.beds_max as BedsMax
                }}
                onChange={({ bedsMin, bedsMax }) => {
                    updateFormValue((oldFormValue) => ({
                        ...oldFormValue,
                        beds_min: bedsMin,
                        beds_max: bedsMax
                    }));
                }}
            />

            <FilterDivider />
            <FiltersLabel>Property type</FiltersLabel>
            {isNewHomesDevelopersSearch ? (
                <SelectPropertyType
                    value={formValue.property_type as PropertyType}
                    onChange={(propertyType) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_type: propertyType
                        }));
                    }}
                />
            ) : (
                <PropertyTypeCheckboxes
                    value={{
                        propertyType: formValue.property_type as PropertyType,
                        propertySubType:
                            formValue.property_sub_type as PropertySubType
                    }}
                    onChange={({ propertySubType, propertyType }) => {
                        updateFormValue((oldFormValue) => ({
                            ...oldFormValue,
                            property_sub_type: propertySubType,
                            property_type: propertyType
                        }));
                    }}
                />
            )}
            <StickyWrapperUpdateButton>
                <UpdateButton
                    isFullWidth
                    type="button"
                    onClick={handlePressUpdate}
                    data-testid="update-results-button"
                    title="Update"
                >
                    {updateResultsText}
                </UpdateButton>
            </StickyWrapperUpdateButton>
        </ModalContentWrapper>
    );
};
